import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUsers, createUser, updateUser, deleteUser, getVendors } from '../services/api';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import UsersTable from '../components/users/UsersTable';
import UserFormDialog from '../components/users/UserFormDialog';

const EMPTY_FORM = {
  username: '', password: '', name: '', role: 'Agent',
  accessLevel: 'all', assignedBankIds: [], canEditBank: true, canUpload: false
};

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [allBanks, setAllBanks] = useState([]);
  const [selectedBanks, setSelectedBanks] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, vendorsRes] = await Promise.all([getUsers(), getVendors()]);
      setUsers(usersRes.data);
      const banks = [];
      vendorsRes.data.forEach(vendor => {
        vendor.banks.forEach(bank => {
          banks.push({ id: bank.id, label: `${vendor.name} - ${bank.bankName} (${bank.region})` });
        });
      });
      setAllBanks(banks);
    } catch (err) {
      console.error('Failed to load users:', err.message);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username, password: '', name: user.name, role: user.role,
        accessLevel: user.accessLevel || 'all', assignedBankIds: user.assignedBankIds || [],
        canEditBank: user.canEditBank !== false, canUpload: user.canUpload === true
      });
      setSelectedBanks(user.assignedBankIds || []);
    } else {
      setEditingUser(null);
      setFormData(EMPTY_FORM);
      setSelectedBanks([]);
    }
    setShowDialog(true);
  };

  const handleCloseDialog = () => { setShowDialog(false); setEditingUser(null); setSelectedBanks([]); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, assignedBankIds: formData.accessLevel === 'specific' ? selectedBanks : [] };
      if (editingUser) {
        if (!data.password) delete data.password;
        await updateUser(editingUser.id, data);
        toast.success('User updated successfully');
      } else {
        if (!data.password) { toast.error('Password is required for new users'); return; }
        await createUser(data);
        toast.success('User created successfully');
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await deleteUser(userId);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  if (loading) {
    return (<><Navbar /><div className="flex items-center justify-center min-h-[calc(100vh-4rem)]"><div className="text-lg">Loading users...</div></div></>);
  }

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">User Management</h1>
            <p className="text-slate-600 mt-1">Manage system users and their permissions</p>
          </div>
          <Button onClick={() => handleOpenDialog()} className="gap-2"><UserPlus className="w-4 h-4" />Add User</Button>
        </div>

        <Card>
          <CardHeader><CardTitle>System Users</CardTitle></CardHeader>
          <CardContent>
            <UsersTable users={users} currentUser={currentUser} onEdit={handleOpenDialog} onDelete={handleDelete} />
          </CardContent>
        </Card>
      </div>

      <UserFormDialog
        open={showDialog} onClose={handleCloseDialog} editingUser={editingUser}
        formData={formData} setFormData={setFormData} allBanks={allBanks}
        selectedBanks={selectedBanks} setSelectedBanks={setSelectedBanks}
        currentUser={currentUser} onSubmit={handleSubmit}
      />
    </>
  );
};

export default UsersPage;
